import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Table, Modal } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

export default function MyOrderDetailPage() {
    const { t } = useContext(TranslatorContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [order] = useState(location.state?.order || null);
    const [marketplaces, setMarketplaces] = useState([]);
    const [selectedImage, setSelectedImage] = useState(
        order?.image_1 ? `${process.env.REACT_APP_IMG_URL}${order.image_1}` : ""
    );
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const statusSteps = ["Pending", "Shipped", "Delivered", "Returned"];

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/market-placer/initial-data`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        },
                    }
                );
                if (response.data?.success && response.data?.data?.market_places) {
                    setMarketplaces(response.data.data.market_places);
                }
            } catch (err) {
                console.error("Error fetching marketplaces:", err);
            }
        };

        fetchInitialData();
    }, []);

    if (!order) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <p className="text-muted">{t("No order data available.")}</p>
                    <Button variant="primary" onClick={() => navigate("/market-placer/my-order")}>
                        {t("Back to Orders")}
                    </Button>
                </div>
            </PageLayout>
        );
    }

    const images = [order.image_1, order.image_2].filter(Boolean);
    const matchedMarketplace = marketplaces.find(
        (m) => String(m.id) === String(order.marketplace_id)
    );

    // Format Date securely
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? "-" : date.toLocaleString();
    };

    // Calculate current status index for timeline
    const currentStatusIndex = statusSteps.findIndex(
        (status) => status.toLowerCase() === (order.delivery_status || "").toLowerCase()
    );

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mc-card mb-4 border-0 shadow-sm">
                        <Card.Body className="mc-breadcrumb d-flex justify-content-between align-items-center py-3">
                            <div>
                                <h3 className="mc-breadcrumb-title mb-1 fw-bold">{t("Order Details")}</h3>
                                <ul className="mc-breadcrumb-list d-flex gap-2 p-0 m-0 list-unstyled small text-muted">
                                    <li><Link to="#" className="text-decoration-none text-muted">{t("Home")}</Link></li>
                                    <li>/</li>
                                    <li>
                                        <Link to="/market-placer/my-order" className="text-decoration-none text-muted">
                                            {t("My Orders")}
                                        </Link>
                                    </li>
                                    <li>/</li>
                                    <li className="text-primary fw-semibold">{order.order_number}</li>
                                </ul>
                            </div>
                            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                                {t("Back")}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Status Timeline */}
                <Col xl={12} className="mb-4">
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-4">
                            <h6 className="fw-bold mb-4">{t("Delivery Status Timeline")}</h6>
                            <div className="d-flex justify-content-between align-items-center position-relative px-2">
                                {/* Connecting Line */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "18px",
                                        left: "10%",
                                        right: "10%",
                                        height: "4px",
                                        backgroundColor: "#e0e0e0",
                                        zIndex: 0,
                                    }}
                                />
                                {statusSteps.map((step, idx) => {
                                    const isCompleted = currentStatusIndex >= 0 && idx <= currentStatusIndex;
                                    const isCurrent = idx === currentStatusIndex;
                                    return (
                                        <div
                                            key={step}
                                            className="text-center position-relative"
                                            style={{ zIndex: 1, flex: 1 }}
                                        >
                                            <div
                                                className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2 fw-bold text-white ${
                                                    isCompleted ? "bg-success" : "bg-secondary opacity-50"
                                                }`}
                                                style={{
                                                    width: "36px",
                                                    height: "36px",
                                                    boxShadow: isCurrent ? "0 0 0 4px rgba(25, 135, 84, 0.25)" : "none",
                                                }}
                                            >
                                                {idx + 1}
                                            </div>
                                            <span
                                                className={`small fw-semibold ${
                                                    isCompleted ? "text-dark" : "text-muted"
                                                }`}
                                            >
                                                {t(step)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Body className="p-3">
                            <div
                                className="text-center mb-3 bg-light rounded p-2"
                                style={{
                                    minHeight: "300px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: selectedImage ? "pointer" : "default",
                                }}
                                onClick={() => selectedImage && setShowPreviewModal(true)}
                            >
                                {selectedImage ? (
                                    <img
                                        src={selectedImage}
                                        alt="Order Doc"
                                        className="img-fluid rounded"
                                        style={{ maxHeight: "280px", objectFit: "contain", width: "100%" }}
                                    />
                                ) : (
                                    <span className="text-muted">{t("No Images")}</span>
                                )}
                            </div>
                            {images.length > 0 && (
                                <div className="d-flex gap-2 justify-content-center">
                                    {images.map((img, idx) => {
                                        const fullUrl = `${process.env.REACT_APP_IMG_URL}${img}`;
                                        return (
                                            <div
                                                key={idx}
                                                onClick={() => setSelectedImage(fullUrl)}
                                                className={`rounded p-1 border ${
                                                    selectedImage === fullUrl ? "border-primary bg-light" : "border-light"
                                                }`}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <img
                                                    src={fullUrl}
                                                    alt={`thumb-${idx}`}
                                                    className="rounded"
                                                    style={{ width: "60px", height: "60px", objectFit: "cover" }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8}>
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold">{t("Order")} #{order.order_number}</h5>
                            <div className="d-flex gap-2">
                                <Badge bg={order.delivery_status === "Delivered" ? "success" : "info"}>
                                    {order.delivery_status || "-"}
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Row className="g-3 mb-4">
                                <Col sm={4}>
                                    <span className="text-muted small d-block">{t("Marketplace")}</span>
                                    <span className="fw-semibold text-dark">
                                        {matchedMarketplace ? matchedMarketplace.name : order.marketplace_id || "-"}
                                    </span>
                                </Col>
                                <Col sm={4}>
                                    <span className="text-muted small d-block">{t("Ordered At")}</span>
                                    <span className="fw-semibold text-dark">
                                        {formatDate(order.ordered_at)}
                                    </span>
                                </Col>
                                <Col sm={2}>
                                    <span className="text-muted small d-block">{t("Subtotal")}</span>
                                    <span className="fw-semibold text-dark">
                                        {order.subtotal ? `$${order.subtotal}` : "-"}
                                    </span>
                                </Col>
                                <Col sm={2}>
                                    <span className="text-muted small d-block">{t("Grand Total")}</span>
                                    <span className="fs-5 text-success fw-bold">
                                        {order.grand_total ? `$${order.grand_total}` : "-"}
                                    </span>
                                </Col>
                            </Row>

                            {order.buyer_details && (
                                <div className="mb-4 p-3 bg-light rounded">
                                    <h6 className="fw-bold mb-2">{t("Buyer Information")}</h6>
                                    <Row className="g-2 small">
                                        <Col sm={6}><strong>{t("Name")}:</strong> {order.buyer_details.name || "-"}</Col>
                                        <Col sm={6}><strong>{t("Email")}:</strong> {order.buyer_details.email || "-"}</Col>
                                        <Col sm={6}><strong>{t("Phone")}:</strong> {order.buyer_details.phone || "-"}</Col>
                                        <Col sm={6}><strong>{t("Country")}:</strong> {order.buyer_details.country || "-"}</Col>
                                        <Col sm={6}><strong>{t("City")}:</strong> {order.buyer_details.city || "-"}</Col>
                                        <Col sm={6}><strong>{t("Address")}:</strong> {order.buyer_details.address || "-"}</Col>
                                    </Row>
                                </div>
                            )}

                            <h6 className="fw-bold mb-3">{t("Ordered Items")}</h6>
                            <Table responsive borderless className="align-middle bg-light rounded">
                                <thead>
                                    <tr className="border-bottom text-muted small">
                                        <th>{t("Product Code")}</th>
                                        <th>{t("Title")}</th>
                                        <th>{t("Qty")}</th>
                                        <th className="text-end">{t("Unit Price")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.products?.length > 0 ? (
                                        order.products.map((item, index) => (
                                            <tr key={index}>
                                                <td className="fw-semibold">{item.product_code || "-"}</td>
                                                <td>{item.title || "-"}</td>
                                                <td>{item.quantity || "-"}</td>
                                                <td className="text-end fw-bold">
                                                    {item.unit_price ? `$${item.unit_price}` : "-"}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center text-muted py-3">
                                                {t("No products found.")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Image Preview Modal */}
            <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t("Image Preview")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <img
                        src={selectedImage}
                        alt="Preview"
                        className="img-fluid rounded"
                        style={{ maxHeight: "70vh", objectFit: "contain" }}
                    />
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}